import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { Auth, getIdToken } from '@angular/fire/auth';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Dealer } from '../../shared/../dealer/shared/dealer.model';
import { DealerService } from '../../shared/../dealer/shared/dealer.service';
import { RemoveCommasPipe } from '../../shared/remove-commas.pipe';
import { DealerNamesComponentState } from '../../shared/state-interface';
import { StateManager } from '../../shared/state-manager.class';

@Component({
    selector: 'app-dealer-names-component',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatAutocompleteModule,
        CommonModule,
        RemoveCommasPipe
    ],
    templateUrl: './dealer-names.component.html',
    styleUrl: './dealer-names.component.css'
})
export class DealerNamesComponent {

    @ViewChild("input") userInput: ElementRef<HTMLInputElement> | undefined;
    @Output() messageEvent = new EventEmitter<string>();

    private auth: Auth = inject(Auth);
    private stateManager: StateManager = inject(StateManager);
    private dealerService: DealerService = inject(DealerService);

    private static _dealerNamesError = "Dealer Names error:";

    protected dealer: FormControl = new FormControl<string | Dealer>("");
    protected filteredDealers: Dealer[] = [];

    private _dealerNames: WritableSignal<Dealer[]> = signal([]);
    private _show: WritableSignal<boolean> = signal(true);

    private _invokingContext: string | undefined;
    private _includeAeroStar: boolean | undefined;
    private _includeInactivated: boolean | undefined;

    public getState() {
        const state: DealerNamesComponentState = {} as DealerNamesComponentState;
        state.invokingContext = this._invokingContext;
        state.includeAeroStar = this._includeAeroStar;
        state.includeInactivated = this._includeInactivated;
        state.dealerControl = this.dealer;
        state.show = this._show();
        return state;
    }

    public restoreState(state: DealerNamesComponentState) {
        this.dealer = state.dealerControl;
        this._dealerNames.set(this.stateManager.getDealerNames(state.invokingContext)!);
        this.init(state.invokingContext, state.includeAeroStar, state.includeInactivated);
        this._show.set(state.show);
    }

    public init(invokingContext?: string, includeAeroStar?: boolean, includeInactivated?: boolean) {
        this._invokingContext = invokingContext;
        this._includeAeroStar = includeAeroStar;
        this._includeInactivated = includeInactivated;

        if (this._dealerNames() == undefined || this._dealerNames().length == 0) {
            const dealerNames = this.stateManager.getDealerNames(this._invokingContext);

            if (dealerNames == undefined) {
                this.dealer.setValue("Loading...");
                this.loadDealerNames();
            } else {
                this._dealerNames.set(dealerNames);
            }
        }
    }

    public get show(): boolean {
        return this._show();
    }

    public set show(value: boolean) {
        this._show.set(value);
    }

    // Note: Must use "arrow" function declaration here
    // in order to have access to 'this'. If using normal
    // function declaration, then 'this' will refer to the
    // mat-select object.
    public displayWithFunc = (dealer: Dealer) => {
        if (dealer instanceof (Dealer)) {
            return dealer.name;
        } else {
            return dealer;
        }
    }

    public filter(): void {
        if (this.userInput != undefined && this._dealerNames().length > 0) {
            const filterValue = this.userInput.nativeElement.value.toLowerCase();
            this.filteredDealers = this._dealerNames().filter(
                dealer =>
                    `${dealer.id} ${dealer.name}`.toLowerCase().includes(filterValue)
            );
        }
    }

    public get dealerCtl(): FormControl {
        return this.dealer;
    }

    public get selectedDealer(): number {
        return this.dealer.value instanceof Dealer ? this.dealer.value.id : -1;
    }

    protected get dealerNames(): Dealer[] {
        return this._dealerNames()!;
    }

    protected async loadDealerNames() {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerService.getDealerNames(await getIdToken(currentUser),
                                            this._includeAeroStar, this._includeInactivated);

            observable.subscribe(response => {
                // For testing to validate proper rendering of form fields
                // when retrieving data over slow internet connections.
                //setTimeout(() => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["get-dealer-names"] == "success") {
                        // this.dealerNames is a signal. The view will be refreshed
                        // upon update to the signal.
                        this._dealerNames.set(Dealer.toDealers(json["dealer-names"]));
                        this.stateManager.setDealerNames(this._dealerNames(), this._invokingContext);
                        this.dealer.setValue("");
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${DealerNamesComponent._dealerNamesError} ${(<Error>error).message}`;
                    console.error(message);
                    this.messageEvent.emit(message);
                }
                //}, 2500);
            })
        }
    }
}